/**
 * Star Rating Input Component
 * Interactive component for selecting a star rating with hover effects
 */

import './StarRating.css';

/**
 * Configuration options for star rating input
 */
export interface StarRatingInputConfig {
  /** Current selected rating (1-5, or null if not rated) */
  currentRating: number | null;
  /** Whether the user is authenticated */
  isAuthenticated: boolean;
  /** Whether ratings can be updated after submission */
  allowUpdate: boolean;
  /** Callback when rating is selected */
  onRatingSelect: (rating: number) => Promise<void>;
  /** Callback when rating is cleared */
  onRatingClear?: () => Promise<void>;
  /** Callback when login is required */
  onLoginRequired?: () => void;
  /** Custom CSS class */
  className?: string;
}

/**
 * StarRatingInput - Interactive component for rating selection
 */
export class StarRatingInput {
  private container: HTMLElement;
  private config: StarRatingInputConfig;
  private hoverRating: number = 0;
  private submitting: boolean = false;

  /**
   * Creates a new StarRatingInput instance
   * @param config - Input configuration
   */
  constructor(config: StarRatingInputConfig) {
    this.config = config;
    this.container = document.createElement('div');
    this.container.className = `komentas-star-rating-input ${config.className || ''}`;
    this.render();
  }

  /**
   * Gets the container element
   */
  getElement(): HTMLElement {
    return this.container;
  }

  /**
   * Updates the configuration and re-renders
   */
  update(config: Partial<StarRatingInputConfig>): void {
    this.config = { ...this.config, ...config };
    this.render();
  }

  /**
   * Renders the star rating input
   */
  private render(): void {
    this.container.innerHTML = '';

    const wrapper = document.createElement('div');
    wrapper.className = 'star-input-wrapper';

    // Title
    const title = document.createElement('div');
    title.className = 'star-input-title';
    if (this.config.currentRating) {
      title.textContent = this.config.allowUpdate 
        ? 'Your rating (click again to clear):'
        : 'Your rating:';
    } else {
      title.textContent = 'Rate this article:';
    }
    wrapper.appendChild(title);

    // Stars container
    const starsContainer = document.createElement('div');
    starsContainer.className = 'star-input-stars';
    
    if (!this.config.isAuthenticated) {
      starsContainer.classList.add('disabled');
    } else if (this.config.currentRating && !this.config.allowUpdate) {
      starsContainer.classList.add('readonly');
    }

    // Create 5 star buttons
    for (let rating = 1; rating <= 5; rating++) {
      const starButton = this.createStarButton(rating);
      starsContainer.appendChild(starButton);
    }

    wrapper.appendChild(starsContainer);

    // Helper text
    const helperText = document.createElement('div');
    helperText.className = 'star-input-helper';
    
    if (!this.config.isAuthenticated) {
      helperText.innerHTML = '<span class="login-required">Please <a href="#" class="login-link">sign in</a> to rate</span>';
      const loginLink = helperText.querySelector('.login-link');
      if (loginLink) {
        loginLink.addEventListener('click', (e) => {
          e.preventDefault();
          if (this.config.onLoginRequired) {
            this.config.onLoginRequired();
          }
        });
      }
    } else if (this.config.currentRating && !this.config.allowUpdate) {
      helperText.textContent = 'Thank you for your rating!';
    } else if (this.submitting) {
      helperText.innerHTML = '<span class="submitting">Submitting...</span>';
    } else if (this.config.currentRating && this.config.allowUpdate) {
      helperText.textContent = 'Click the same star to clear your rating';
    } else {
      helperText.textContent = 'Click a star to rate';
    }

    wrapper.appendChild(helperText);

    this.container.appendChild(wrapper);
  }

  /**
   * Creates a star button
   */
  private createStarButton(rating: number): HTMLElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'star-button';
    button.setAttribute('aria-label', `Rate ${rating} star${rating > 1 ? 's' : ''}`);
    button.dataset.rating = rating.toString();

    // Set initial state
    this.updateStarButton(button, rating);

    // Event listeners
    if (this.config.isAuthenticated && (this.config.allowUpdate || !this.config.currentRating)) {
      button.addEventListener('mouseenter', () => this.handleStarHover(rating));
      button.addEventListener('mouseleave', () => this.handleStarLeave());
      button.addEventListener('click', () => this.handleStarClick(rating));
      button.addEventListener('focus', () => this.handleStarHover(rating));
      button.addEventListener('blur', () => this.handleStarLeave());
    } else {
      button.disabled = true;
    }

    return button;
  }

  /**
   * Updates a star button's appearance
   */
  private updateStarButton(button: HTMLElement, rating: number): void {
    const currentRating = this.config.currentRating || 0;
    const displayRating = this.hoverRating > 0 ? this.hoverRating : currentRating;

    if (rating <= displayRating) {
      button.textContent = '★'; // Filled star
      button.classList.add('filled');
      button.classList.remove('empty');
    } else {
      button.textContent = '☆'; // Empty star
      button.classList.add('empty');
      button.classList.remove('filled');
    }

    // Highlight on hover
    if (this.hoverRating > 0 && rating <= this.hoverRating) {
      button.classList.add('hover');
    } else {
      button.classList.remove('hover');
    }
  }

  /**
   * Handles star hover
   */
  private handleStarHover(rating: number): void {
    if (this.submitting) return;
    
    this.hoverRating = rating;
    this.updateAllStars();
  }

  /**
   * Handles mouse leave
   */
  private handleStarLeave(): void {
    if (this.submitting) return;
    
    this.hoverRating = 0;
    this.updateAllStars();
  }

  /**
   * Handles star click
   */
  private async handleStarClick(rating: number): Promise<void> {
    if (this.submitting) return;
    if (!this.config.isAuthenticated) {
      if (this.config.onLoginRequired) {
        this.config.onLoginRequired();
      }
      return;
    }

    // If user clicks the same star they already selected, clear the rating
    if (this.config.currentRating === rating && this.config.allowUpdate) {
      this.submitting = true;
      this.render(); // Show submitting state

      try {
        if (this.config.onRatingClear) {
          await this.config.onRatingClear();
          this.config.currentRating = null;
        }
        this.submitting = false;
        this.render();
      } catch (error) {
        console.error('Failed to clear rating:', error);
        this.submitting = false;
        this.render();
        // Could show error message here
      }
      return;
    }

    this.submitting = true;
    this.render(); // Show submitting state

    try {
      await this.config.onRatingSelect(rating);
      this.config.currentRating = rating;
      this.submitting = false;
      this.render();
    } catch (error) {
      console.error('Failed to submit rating:', error);
      this.submitting = false;
      this.render();
      // Could show error message here
    }
  }

  /**
   * Updates all star buttons
   */
  private updateAllStars(): void {
    const stars = this.container.querySelectorAll('.star-button');
    stars.forEach((star) => {
      const rating = parseInt((star as HTMLElement).dataset.rating || '0');
      this.updateStarButton(star as HTMLElement, rating);
    });
  }

  /**
   * Destroys the component and cleans up event listeners
   */
  destroy(): void {
    this.container.innerHTML = '';
  }
}
